import React from 'react'
import { Link } from 'react-router-dom'

import NavBar from '../landing/navbar'
import NavBarContent from '../landing/navbar/NavBarContent'
import Footer from '../landing/footer'
import Button, { ButtonSize } from '../../components/ui/button'
import type { BlogTableOfContentsItem } from './types'
import './blog.scss'

export type ArticlePageLayoutProps = {
  documentTitle?: string
  backTo: string
  backLabel: string
  title: string
  date?: string
  image?: string
  tableOfContents?: BlogTableOfContentsItem[]
  children: React.ReactNode
  /** Plain anchors instead of React Router (static HTML generation). */
  staticLinks?: boolean
}

const ArticlePageLayout: React.FC<ArticlePageLayoutProps> = ({
  documentTitle,
  backTo,
  backLabel,
  title,
  date,
  image,
  tableOfContents,
  children,
  staticLinks,
}) => {
  const hasToc = Boolean(tableOfContents?.length)

  React.useEffect(() => {
    if (!staticLinks && documentTitle) {
      document.title = documentTitle
    }
  }, [documentTitle, staticLinks])

  const backControl = staticLinks ? (
    <a href={backTo}>{backLabel}</a>
  ) : (
    <Link to={backTo}>{backLabel}</Link>
  )

  const createInvoiceCta = staticLinks ? (
    <a href='/login' className='btn btn-primary btn-lg button button--width-fixed'>
      Create Invoice for FREE
    </a>
  ) : (
    <Link to='/login'>
      <Button size={ButtonSize.LARGE}>Create Invoice for FREE</Button>
    </Link>
  )

  return (
    <div className='blog-page'>
      <header role='banner'>
        {staticLinks ? (
          <NavBarContent staticLinks />
        ) : (
          <NavBar />
        )}
      </header>
      <main
        className={`container py-5 blog-post-page${hasToc ? ' blog-post-page--with-toc' : ''}`}
        role='main'
      >
        <article className={hasToc ? 'blog-post-article blog-post-article--with-toc' : 'blog-post-article'}>
          <div className='blog-post-layout'>
            <div className='blog-post-layout__main'>
              <p className='blog-post-back mb-3'>{backControl}</p>
              {image ? (
                <div className='blog-post-hero-wrap'>
                  <img src={image} alt='' className='blog-post-hero-image' />
                </div>
              ) : null}
              <header className='blog-post-header'>
                <h1 className='blog-post-title'>{title}</h1>
                {date ? <time dateTime={date} className='blog-post-date'>{date}</time> : null}
              </header>
              <div className='blog-post-body'>{children}</div>
            </div>
            {hasToc && tableOfContents ? (
              <aside className='blog-post-toc d-none d-xl-block' aria-label='Table of contents'>
                <nav className='blog-post-toc-nav'>
                  <p className='blog-post-toc-heading'>Table of Contents</p>
                  <ul className='blog-post-toc-list'>
                    {tableOfContents.map((item) => (
                      <li key={item.id}>
                        <a href={`#${item.id}`}>{item.label}</a>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className='d-flex flex-column text-center gap-2 mt-5'>
                  {createInvoiceCta}
                  <small><i>No credit card required</i></small>
                </div>
              </aside>
            ) : null}
          </div>
        </article>
      </main>
      <Footer staticLinks={staticLinks} />
    </div>
  )
}

export default ArticlePageLayout
